import { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Plus, AlertTriangle, Eye, Filter, Calendar } from 'lucide-react';
import { useMyMedRequests, MedRequest, getStatusLabel, getStatusColor, getReasonLabel, getPriorityLabel, getPriorityColor } from '../../hooks/useMed';

export function MedRequestList() {
  const navigate = useNavigate();
  const [filters, setFilters] = useState({ status: '', page: 1, limit: 10 });
  const { data, isLoading, error } = useMyMedRequests(filters);

  const formatDate = (dateStr: string) => new Date(dateStr).toLocaleDateString('pt-BR');
  const formatCurrency = (cents: number) => (cents / 100).toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' });

  const StatusBadge = ({ status }: { status: string }) => {
    const color = getStatusColor(status);
    const colorClasses: Record<string, string> = {
      yellow: 'bg-yellow-100 text-yellow-800',
      blue: 'bg-blue-100 text-blue-800',
      green: 'bg-green-100 text-green-800',
      red: 'bg-red-100 text-red-800',
      gray: 'bg-gray-100 text-gray-800',
    };
    return <span className={`px-2 py-1 rounded-full text-xs font-medium ${colorClasses[color]}`}>{getStatusLabel(status)}</span>;
  };

  return (
    <div className="p-6">
      <div className="flex justify-between items-center mb-6">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Minhas Solicitações MED</h1>
          <p className="text-gray-600">Mecanismo Especial de Devolução PIX</p>
        </div>
        <Link to="/banking/med/new" className="flex items-center gap-2 px-4 py-2 bg-orange-600 text-white rounded-lg hover:bg-orange-700">
          <Plus className="w-4 h-4" /> Nova Solicitação
        </Link>
      </div>

      <div className="bg-white rounded-lg shadow-sm border p-4 mb-6">
        <div className="flex items-center gap-4">
          <Filter className="w-4 h-4 text-gray-400" />
          <select
            value={filters.status}
            onChange={(e) => setFilters({ ...filters, status: e.target.value, page: 1 })}
            className="px-3 py-2 border border-gray-300 rounded-lg"
          >
            <option value="">Todos os status</option>
            <option value="PENDING">Pendente</option>
            <option value="UNDER_REVIEW">Em Análise</option>
            <option value="APPROVED">Aprovado</option>
            <option value="REJECTED">Rejeitado</option>
            <option value="COMPLETED">Concluído</option>
          </select>
        </div>
      </div>

      <div className="bg-white rounded-lg shadow-sm border overflow-hidden">
        {isLoading ? (
          <div className="p-8 text-center text-gray-500">Carregando...</div>
        ) : error ? (
          <div className="p-8 text-center text-red-500">Erro ao carregar solicitações</div>
        ) : !data?.data?.length ? (
          <div className="p-8 text-center text-gray-500">
            <AlertTriangle className="w-12 h-12 mx-auto mb-4 text-gray-300" />
            <p>Nenhuma solicitação encontrada</p>
          </div>
        ) : (
          <table className="w-full">
            <thead className="bg-gray-50 border-b">
              <tr>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Data</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Motivo</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Valor</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Status</th>
                <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase">Ações</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-200">
              {data.data.map((item: MedRequest) => (
                <tr key={item.id} className="hover:bg-gray-50">
                  <td className="px-4 py-4">
                    <div className="flex items-center gap-2">
                      <Calendar className="w-4 h-4 text-gray-400" />
                      {formatDate(item.createdAt)}
                    </div>
                  </td>
                  <td className="px-4 py-4">{getReasonLabel(item.reason)}</td>
                  <td className="px-4 py-4 font-medium">{formatCurrency(item.requestedAmount)}</td>
                  <td className="px-4 py-4">
                    <StatusBadge status={item.status} />
                    {item.daysRemaining !== undefined && item.daysRemaining <= 7 && item.status === 'PENDING' && (
                      <span className="ml-2 text-xs text-red-500">{item.daysRemaining}d restantes</span>
                    )}
                  </td>
                  <td className="px-4 py-4 text-right">
                    <button onClick={() => navigate(`/banking/med/${item.id}`)} className="p-2 text-gray-400 hover:text-gray-600" title="Ver detalhes">
                      <Eye className="w-4 h-4" />
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
    </div>
  );
}

export default MedRequestList;
